/**
 * OTP Input Component
 *
 * 6-digit OTP code input with auto-focus and paste support.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import styles from './OtpInput.module.css';
import { useSettings } from '@/contexts/SettingsContext';
import { COMPONENT_AUTH } from '@/constants/translations';

export interface OtpInputProps {
  /** Number of OTP digits (default: 6) */
  length?: number;
  /** Allow alphanumeric characters (A-Z, 0-9). Default: false (numeric only) */
  alphanumeric?: boolean;
  /** Callback when OTP is complete */
  onComplete: (otp: string) => void;
  /** Callback when OTP value changes */
  onChange?: (otp: string) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Auto focus on mount */
  autoFocus?: boolean;
}

/**
 * OTP Input with individual digit boxes
 *
 * @example
 * ```tsx
 * <OtpInput
 *   onComplete={(otp) => handleVerify(otp)}
 *   error={error}
 *   autoFocus
 * />
 * ```
 */
export const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  alphanumeric = false,
  onComplete,
  onChange,
  disabled = false,
  error,
  autoFocus = true,
}) => {
  const { settings } = useSettings();
  const t = COMPONENT_AUTH[settings.language];
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Handle digit input
  const handleChange = useCallback(
    (index: number, value: string) => {
      const digit = alphanumeric
        ? value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-1)
        : value.replace(/\D/g, '').slice(-1);

      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);

      const otpString = newOtp.join('');
      onChange?.(otpString);

      // Move to next input if digit entered
      if (digit && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Check if OTP is complete
      if (otpString.length === length && !otpString.includes('')) {
        onComplete(otpString);
      }
    },
    [otp, length, onChange, onComplete, alphanumeric]
  );

  // Handle backspace
  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        // Move to previous input on backspace if current is empty
        inputRefs.current[index - 1]?.focus();
      }
    },
    [otp]
  );

  // Handle paste
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const raw = e.clipboardData.getData('text');
      const pastedData = alphanumeric
        ? raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, length)
        : raw.replace(/\D/g, '').slice(0, length);

      if (pastedData) {
        const newOtp = Array(length).fill('');
        pastedData.split('').forEach((digit, i) => {
          if (i < length) {
            newOtp[i] = digit;
          }
        });
        setOtp(newOtp);

        const otpString = newOtp.join('');
        onChange?.(otpString);

        // Focus last filled input or next empty
        const lastIndex = Math.min(pastedData.length, length) - 1;
        inputRefs.current[lastIndex]?.focus();

        // Check if complete
        if (otpString.length === length) {
          onComplete(otpString);
        }
      }
    },
    [length, onChange, onComplete, alphanumeric]
  );

  // Reset OTP
  const reset = useCallback(() => {
    setOtp(Array(length).fill(''));
    inputRefs.current[0]?.focus();
  }, [length]);

  // Expose reset method
  useEffect(() => {
    (window as unknown as Record<string, unknown>).resetOtp = reset;
    return () => {
      delete (window as unknown as Record<string, unknown>).resetOtp;
    };
  }, [reset]);

  return (
    <div className={styles.container}>
      <div className={styles.inputGroup} role="group" aria-label={t.otpInputGroup}>
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode={alphanumeric ? 'text' : 'numeric'}
            pattern={alphanumeric ? '[A-Z0-9]*' : '[0-9]*'}
            maxLength={1}
            value={otp[index]}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            className={`${styles.input} ${error ? styles.error : ''}`}
            aria-label={t.otpInputDigit(index)}
            autoComplete="one-time-code"
          />
        ))}
      </div>
      {error && (
        <p className={styles.errorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default OtpInput;
